import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trainingEventService } from '../../services/trainingEventService';
import { useNotifyDataChange } from '../../hooks/useNotifyDataChange';
import EmptyState from '../common/EmptyState';
import { STATUS_LABELS, getStatusClass } from './EditEventModal';
import './TrainingEvents.css';
import './TrainingEventDetail.css';

// Lazy loading для модальных окон
const AddParticipantsModal = lazy(() => import('./AddParticipantsModal'));
const EditEventModal = lazy(() => import('./EditEventModal'));

// SVG иконки
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3l4 4-7 7H10v-4l7-7z" />
    <path d="M4 20h16" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const UserMinusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TrainingEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Состояния для модальных окон
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddParticipantsModal, setShowAddParticipantsModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  
  // Состояния для управления участниками
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [removingParticipants, setRemovingParticipants] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Флаги для предотвращения дублирования запросов
  const isInitialMount = useRef(true);
  const isLoadingEvent = useRef(false);

  const { notifyDataChanged } = useNotifyDataChange();

  // Загрузка мероприятия с защитой от дублирования
  const fetchEvent = useCallback(async (forceRefresh = false) => {
    if (isLoadingEvent.current) {
      return;
    }
    
    isLoadingEvent.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const data = await trainingEventService.getById(id);
      setEvent(data);
      setSelectedParticipantIds([]);
    } catch (err) {
      console.error('Ошибка загрузки мероприятия:', err);
      setError(err.message || 'Ошибка загрузки мероприятия');
    } finally {
      setLoading(false);
      isLoadingEvent.current = false;
    }
  }, [id]);

  // Загрузка данных только при первом монтировании
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchEvent();
    }
  }, [fetchEvent]);

  // Показ уведомления
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Переключение выбора участника для удаления
  const toggleParticipantSelection = (participantId) => {
    setSelectedParticipantIds(prev => {
      if (prev.includes(participantId)) {
        return prev.filter(id => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  // Выбрать всех участников для удаления
  const selectAllParticipants = () => {
    if (event?.participants) {
      setSelectedParticipantIds(event.participants.map(p => p.id));
    }
  };

  // Снять выбор со всех участников
  const deselectAllParticipants = () => {
    setSelectedParticipantIds([]);
  };

  // Подтверждение удаления участников
  const handleConfirmRemoveParticipants = () => {
    if (selectedParticipantIds.length === 0) {
      showToast('Выберите хотя бы одного участника для удаления', 'error');
      return;
    }
    setShowRemoveConfirm(true);
  };

  // Удаление выбранных участников
  const handleRemoveParticipants = async () => {
    setRemovingParticipants(true);
    try {
      const removedEmployees = event.participants.filter(p => selectedParticipantIds.includes(p.id));
      
      await trainingEventService.removeParticipants(id, selectedParticipantIds);
      showToast(`Успешно удалено ${selectedParticipantIds.length} участников`, 'success');
      
      // Уведомляем приложение об удалении участников
      notifyDataChanged('TrainingEventDetail', {
        action: 'removeParticipants',
        eventId: id,
        courseId: event.course_id,
        count: selectedParticipantIds.length,
        participants: removedEmployees.map(p => ({
          id: p.id,
          name: p.employee_name
        }))
      });
      
      setShowRemoveConfirm(false);
      setSelectedParticipantIds([]);
      fetchEvent(true);
    } catch (err) {
      console.error('Ошибка удаления участников:', err);
      showToast(err.message || 'Ошибка при удалении участников', 'error');
      setShowRemoveConfirm(false);
    } finally {
      setRemovingParticipants(false);
    }
  };

  // Удаление мероприятия
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await trainingEventService.delete(id);
      showToast('Мероприятие успешно удалено', 'success');
      
      // Уведомляем приложение об удалении мероприятия
      notifyDataChanged('TrainingEventDetail', {
        action: 'delete',
        entity: 'trainingEvent',
        eventId: id,
        courseId: event.course_id,
        title: event.title
      });
      
      setTimeout(() => navigate('/training-events'), 1000);
    } catch (err) {
      console.error('Ошибка удаления мероприятия:', err);
      showToast(err.message || 'Ошибка при удалении мероприятия', 'error');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return '—';
    if (startDate === endDate) return formatDate(startDate);
    return `${formatDate(startDate)} — ${formatDate(endDate)}`;
  };

  const getFormatText = (format) => {
    switch (format) {
      case 'online': return 'Онлайн';
      case 'onsite': return 'Оффлайн';
      case 'hybrid': return 'Гибрид';
      default: return format || '—';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Черновик';
      case 'planned': return 'Запланировано';
      case 'confirmed': return 'Подтверждено';
      case 'in_progress': return 'В процессе';
      case 'completed': return 'Завершено';
      case 'cancelled': return 'Отменено';
      default: return status || '—';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'status-draft';
      case 'planned': return 'status-planned';
      case 'confirmed': return 'status-confirmed';
      case 'in_progress': return 'status-in-progress';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getParticipantStatusBadgeClass = (status) => {
    switch (status) {
      case 'registered': return 'participant-status-registered';
      case 'confirmed': return 'participant-status-confirmed';
      case 'attended': return 'participant-status-attended';
      case 'completed': return 'participant-status-completed';
      case 'cancelled': return 'participant-status-cancelled';
      case 'no_show': return 'participant-status-no-show';
      default: return 'participant-status-default';
    }
  };

  const getParticipantStatusText = (status) => {
    switch (status) {
      case 'registered': return 'Зарегистрирован';
      case 'confirmed': return 'Подтверждён';
      case 'attended': return 'Присутствовал';
      case 'completed': return 'Завершил';
      case 'cancelled': return 'Отменён';
      case 'no_show': return 'Не явился';
      default: return status || '—';
    }
  };

  // Кнопка "Назад" всегда возвращает на /training-events
  const handleBack = () => {
    navigate('/events');
  };

  // Обработчики для модальных окон
  const handleEventUpdated = () => {
    setShowEditModal(false);
    fetchEvent(true);
  };

  const handleParticipantsAdded = () => {
    setShowAddParticipantsModal(false);
    fetchEvent(true);
  };

  if (loading) {
    return (
      <div className="events-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка мероприятия...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Ошибка загрузки</h3>
          <p>{error}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button className="btn-secondary" onClick={handleBack}>
              <span style={{ marginRight: '6px' }}><ArrowLeftIcon /></span>
              Вернуться к списку
            </button>
            <button className="btn-primary" onClick={() => fetchEvent(true)}>
              Повторить попытку
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="events-container">
        <EmptyState
          icon="📅"
          title="Мероприятие не найдено"
          subtitle="Возможно, оно было удалено или указан неверный ID"
        >
          <button className="btn-primary" onClick={handleBack}>
            <span style={{ marginRight: '6px' }}><ArrowLeftIcon /></span>
            Вернуться к списку
          </button>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="events-container">
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">
            {toast.type === 'success' ? <CheckIcon /> : <AlertIcon />}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}

      {/* Шапка с навигацией */}
      <div className="events-header">
        <div className="header-navigation">
          <button className="btn-secondary" onClick={handleBack} title="Вернуться к списку">
            <ArrowLeftIcon />
          </button>
          <h2>{event.title}</h2>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowEditModal(true)}>
            <span style={{ marginRight: '6px' }}><EditIcon /></span>
            Редактировать
          </button>
          <button 
            className="btn-secondary btn-delete" 
            onClick={() => setShowDeleteConfirm(true)}
          >
            <span style={{ marginRight: '6px' }}><TrashIcon /></span>
            Удалить
          </button>
        </div>
      </div>

      {/* Содержимое */}
      <div className="event-detail-content">
        {/* Основная информация */}
        <div className="detail-section">
          <h3>Основная информация</h3>
          
          <div className="detail-grid">
            <div>
              <div className="detail-label">Вид обучения</div>
              <div className="detail-value">
                {event.course_name || 'Не указан'}
              </div>
            </div>
            
            <div>
              <div className="detail-label">Формат</div>
              <div className="detail-value">
                <span className={`format-badge format-${event.format}`}>{getFormatText(event.format)}</span>
              </div>
            </div>
            
            <div>
              <div className="detail-label">Статус</div>
              <div className="detail-value">
                <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                  {getStatusText(event.status)}
                </span>
              </div>
            </div>
            
            <div>
              <div className="detail-label">Период проведения</div>
              <div className="detail-value">
                {formatDateRange(event.start_date, event.end_date)}
              </div>
            </div>
            
            <div>
              <div className="detail-label">Адрес/Место</div>
              <div className="detail-value">
                {event.location || 'Не указан'}
              </div>
            </div>
            
            <div>
              <div className="detail-label">Учебный центр</div>
              <div className="detail-value">
                {event.training_center || 'Не указан'}
              </div>
            </div>
            
            <div>
              <div className="detail-label">Стоимость</div>
              <div className="detail-value">
                {event.cost ? `${Number(event.cost).toLocaleString()} ₽` : 'Не указана'}
              </div>
            </div>
            
            <div>
              <div className="detail-label">Участники</div>
              <div className="detail-value">
                {event.participants_count || 0} / {event.max_participants || 0} мест
              </div>
            </div>
          </div>
        </div>

        {/* Заметки */}
        {event.notes && (
          <div className="detail-section">
            <h3>Заметки</h3>
            <div className="detail-notes">
              {event.notes}
            </div>
          </div>
        )}

        {/* Участники - Табличное отображение */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>
              Участники ({event.participants_count || 0})
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              {selectedParticipantIds.length > 0 && (
                <button 
                  className="btn-secondary btn-delete"
                  onClick={handleConfirmRemoveParticipants}
                  disabled={removingParticipants}
                >
                  <span style={{ marginRight: '6px' }}><UserMinusIcon /></span>
                  Удалить ({selectedParticipantIds.length})
                </button>
              )}
              <button 
                className="btn-primary"
                onClick={() => setShowAddParticipantsModal(true)}
              >
                <span style={{ marginRight: '6px' }}><PlusIcon /></span>
                Добавить участников
              </button>
            </div>
          </div>
          
          {event.participants && event.participants.length > 0 ? (
            <>
              {/* Панель действий с участниками */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  {selectedParticipantIds.length > 0 
                    ? `Выбрано: ${selectedParticipantIds.length} из ${event.participants.length}`
                    : 'Выберите участников для удаления'
                  }
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    className="btn-secondary btn-sm"
                    onClick={selectAllParticipants}
                  >
                    Выбрать всех
                  </button>
                  <button 
                    className="btn-secondary btn-sm"
                    onClick={deselectAllParticipants}
                    disabled={selectedParticipantIds.length === 0}
                  >
                    Снять выбор
                  </button>
                </div>
              </div>
              
              <div className="participants-table-wrapper">
                <table className="participants-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>№</th>
                      <th style={{ width: '50px' }}>
                        <input
                          type="checkbox"
                          checked={event.participants.length > 0 && selectedParticipantIds.length === event.participants.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllParticipants();
                            } else {
                              deselectAllParticipants();
                            }
                          }}
                        />
                      </th>
                      <th>ФИО</th>
                      <th>Таб. №</th>
                      <th>Должность</th>
                      <th>Подразделение</th>
                      <th>Статус</th>
                      <th>Дата прохождения</th>
                      <th>Удостоверение</th>
                      <th>Примечания</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.participants.map((participant, index) => (
                      <tr 
                        key={participant.id}
                        onClick={() => toggleParticipantSelection(participant.id)}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedParticipantIds.includes(participant.id) ? '#fee2e2' : 'transparent'
                        }}
                      >
                        <td className="cell-number">{index + 1}</td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedParticipantIds.includes(participant.id)}
                            onChange={() => toggleParticipantSelection(participant.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="cell-name">{participant.employee_name}</td>
                        <td className="cell-personnel">{participant.personnel_number || '—'}</td>
                        <td className="cell-position">{participant.position || '—'}</td>
                        <td className="cell-department">{participant.department || '—'}</td>
                        <td>
                          <span className={`participant-status-badge ${getParticipantStatusBadgeClass(participant.status)}`}>
                            {getParticipantStatusText(participant.status)}
                          </span>
                        </td>
                        <td className="cell-date">
                          {participant.completion_date ? formatDate(participant.completion_date) : '—'}
                        </td>
                        <td className="cell-certificate">
                          {participant.certificate_number || '—'}
                        </td>
                        <td className="cell-notes">
                          {participant.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="participants-empty">
              <div className="participants-empty-icon">👥</div>
              <div className="participants-empty-text">
                Нет добавленных участников
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lazy loaded модальные окна */}
      {showEditModal && (
        <Suspense fallback={null}>
          <EditEventModal
            isOpen={showEditModal}
            event={event}
            onClose={() => setShowEditModal(false)}
            onSuccess={handleEventUpdated}
          />
        </Suspense>
      )}

      {showAddParticipantsModal && (
        <Suspense fallback={null}>
          <AddParticipantsModal
            isOpen={showAddParticipantsModal}
            event={event}
            onClose={() => setShowAddParticipantsModal(false)}
            onSuccess={handleParticipantsAdded}
          />
        </Suspense>
      )}

      {/* Модальное окно подтверждения удаления участников */}
      {showRemoveConfirm && (
        <div className="modal-overlay" onClick={() => !removingParticipants && setShowRemoveConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Подтверждение удаления участников</h3>
              <button className="modal-close" onClick={() => setShowRemoveConfirm(false)} disabled={removingParticipants}>
                <XIcon />
              </button>
            </div>
            <div className="modal-body">
              <p className="confirm-message">
                Вы уверены, что хотите удалить {selectedParticipantIds.length} участников из мероприятия "{event.title}"?
              </p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowRemoveConfirm(false)}
                disabled={removingParticipants}
              >
                Отмена
              </button>
              <button 
                className="btn-primary btn-danger" 
                onClick={handleRemoveParticipants}
                disabled={removingParticipants}
              >
                <span style={{ marginRight: '6px' }}><TrashIcon /></span>
                {removingParticipants ? 'Удаление...' : 'Удалить участников'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления мероприятия */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Подтверждение удаления</h3>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                <XIcon />
              </button>
            </div>
            <div className="modal-body">
              <p className="confirm-message">
                Вы уверены, что хотите удалить мероприятие "{event.title}"? 
                Это действие нельзя отменить.
              </p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Отмена
              </button>
              <button 
                className="btn-primary btn-danger" 
                onClick={handleDelete}
                disabled={deleting}
              >
                <span style={{ marginRight: '6px' }}><TrashIcon /></span>
                {deleting ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingEventDetail;